import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Lock, ShieldCheck, X, Mail, ArrowLeft, Check } from "lucide-react";
import { useApp, API } from "../context";
import { SparkleField } from "./shared";
import { showApiError } from "../utils/showApiError";

// Session-scoped unlock, timeout-aware. We store the unlock TIMESTAMP so an
// optional parent-set auto-lock timeout can expire it; with no timeout it
// simply lasts until the browser tab is closed. Stored per-account.
const sessionKey = (id) => `parent_pin_unlocked::${id || "anon"}`;

function unlockAgeMs(parentId) {
  try {
    const raw = sessionStorage.getItem(sessionKey(parentId));
    if (!raw) return null;
    const t = parseInt(raw, 10);
    return Number.isFinite(t) ? Date.now() - t : null;
  } catch { return null; }
}
export function isPinUnlocked(parentId, timeoutMin = 0) {
  const age = unlockAgeMs(parentId);
  if (age == null) return false;
  if (timeoutMin > 0 && age > timeoutMin * 60000) { clearPinUnlock(parentId); return false; }
  return true;
}
export function markPinUnlocked(parentId) {
  try { sessionStorage.setItem(sessionKey(parentId), String(Date.now())); } catch { /* no-op */ }
}
export function clearPinUnlock(parentId) {
  try { sessionStorage.removeItem(sessionKey(parentId)); } catch { /* no-op */ }
}

// Shared gate logic: fetches PIN status + timeout, tracks unlock state, and
// auto re-locks when the timeout elapses (timer + on tab focus).
function usePinGate(parentId, area, detail) {
  const [state, setState] = useState("checking"); // checking | locked | open
  const [timeoutMin, setTimeoutMin] = useState(0);
  const timerRef = useRef(null);

  const scheduleRelock = useCallback((tmin) => {
    clearTimeout(timerRef.current);
    if (tmin > 0) {
      const age = unlockAgeMs(parentId);
      if (age != null) {
        const remaining = tmin * 60000 - age;
        if (remaining <= 0) { clearPinUnlock(parentId); setState("locked"); return; }
        timerRef.current = setTimeout(() => { clearPinUnlock(parentId); setState("locked"); }, remaining);
      }
    }
  }, [parentId]);

  useEffect(() => {
    let alive = true;
    if (!parentId) { setState("open"); return; }
    axios.get(`${API}/parent/pin/status/${parentId}`)
      .then(r => {
        if (!alive) return;
        const locked = !!r.data?.locked;
        const tmin = Number(r.data?.pin_timeout_minutes || 0);
        setTimeoutMin(tmin);
        if (!locked) { setState("open"); return; }
        if (isPinUnlocked(parentId, tmin)) { setState("open"); scheduleRelock(tmin); }
        else setState("locked");
      })
      .catch(() => { if (alive) setState("open"); });
    return () => { alive = false; clearTimeout(timerRef.current); };
  }, [parentId, scheduleRelock]);

  // Re-check on tab focus (covers the timeout elapsing while backgrounded).
  useEffect(() => {
    const check = () => {
      if (state === "open" && timeoutMin > 0 && !isPinUnlocked(parentId, timeoutMin)) setState("locked");
    };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    return () => { window.removeEventListener("focus", check); document.removeEventListener("visibilitychange", check); };
  }, [state, timeoutMin, parentId]);

  const unlock = useCallback(() => {
    markPinUnlocked(parentId);
    setState("open");
    scheduleRelock(timeoutMin);
    if (parentId && area) {
      axios.post(`${API}/parent/pin/log-unlock`, { parent_id: parentId, area, detail: detail || "" }).catch(() => {});
    }
  }, [parentId, timeoutMin, scheduleRelock, area, detail]);

  return { state, unlock };
}

/**
 * Full-page gate, wraps a whole protected page (Parent Controls, Test Mode).
 */
export default function PinGate({ children, label = "this area", testId = "pin-gate", area }) {
  const { user } = useApp();
  const parentId = user?.id || user?.email || "";
  const { state, unlock } = usePinGate(parentId, area || testId);

  if (state === "checking") {
    return (
      <div className="page-universe flex items-center justify-center min-h-screen" data-testid={`${testId}-loading`}>
        <div className="universe-card text-center p-8"><Lock className="mx-auto text-white/30 mb-3 animate-pulse" size={36} /><p className="text-white/50 text-sm">Checking...</p></div>
      </div>
    );
  }
  if (state === "locked") {
    return (
      <div className="page-universe min-h-screen flex items-center justify-center px-4 py-16" data-testid={`${testId}-locked`}>
        <SparkleField count={12} />
        <div className="relative w-full max-w-sm universe-card p-6 text-center">
          <PinPanel parentId={parentId} label={label} onUnlock={unlock} testId={testId} />
        </div>
      </div>
    );
  }
  return children;
}

/**
 * Inline gate, locks a SECTION inside an otherwise-open page (e.g. report
 * sharing inside Parent Corner). Shows a compact locked card with an Unlock
 * button that opens the PIN modal; renders children once unlocked.
 */
export function PinGuard({ children, label = "this area", testId = "pin-guard", lockedTitle, lockedDesc, area, detail }) {
  const { user } = useApp();
  const parentId = user?.id || user?.email || "";
  const { state, unlock } = usePinGate(parentId, area || testId, detail);
  const [showModal, setShowModal] = useState(false);

  if (state === "open" || state === "checking") return children;

  return (
    <div className="universe-card text-center py-8 px-4" data-testid={`${testId}-locked`}>
      <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-amber-400/30 flex items-center justify-center mx-auto mb-3">
        <Lock size={22} className="text-amber-300" />
      </div>
      <h3 className="text-white font-fredoka font-bold text-base">{lockedTitle || "Locked with your Parent PIN"}</h3>
      <p className="text-white/40 text-xs mt-1 mb-4 max-w-xs mx-auto">{lockedDesc || `Enter your PIN to open ${label}.`}</p>
      <button onClick={() => setShowModal(true)} data-testid={`${testId}-unlock-btn`}
        className="px-5 py-2.5 rounded-xl bg-amber-500 text-white font-fredoka font-bold text-sm hover:bg-amber-400 transition-all inline-flex items-center gap-2">
        <ShieldCheck size={16} /> Unlock
      </button>
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid={`${testId}-modal`} onClick={() => setShowModal(false)}>
          <div className="relative w-full max-w-sm universe-card p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowModal(false)} data-testid={`${testId}-modal-close`} className="absolute top-3 right-3 text-white/40 hover:text-white/70"><X size={18} /></button>
            <PinPanel parentId={parentId} label={label} testId={testId} onUnlock={() => { setShowModal(false); unlock(); }} />
          </div>
        </div>
      )}
    </div>
  );
}

// Shared enter/reset panel used by both the full-page gate and the modal.
function PinPanel({ parentId, label, onUnlock, testId }) {
  const [view, setView] = useState("enter"); // enter | reset
  return view === "enter"
    ? <EnterPin parentId={parentId} label={label} onUnlock={onUnlock} onForgot={() => setView("reset")} testId={testId} />
    : <ResetPin parentId={parentId} onBack={() => setView("enter")} onDone={onUnlock} testId={testId} />;
}

function EnterPin({ parentId, label, onUnlock, onForgot, testId }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (pin.length !== 4) { setError("Enter your 4-digit PIN."); return; }
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/parent/pin/verify`, { parent_id: parentId, pin });
      onUnlock();
    } catch (err) {
      setError(err.response?.data?.detail || "That PIN is incorrect. Try again.");
      setPin("");
      inputRef.current?.focus();
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid={`${testId}-enter-form`}>
      <div className="w-14 h-14 rounded-2xl bg-amber-500/20 border border-amber-400/30 flex items-center justify-center mx-auto mb-4">
        <Lock size={26} className="text-amber-300" />
      </div>
      <h2 className="text-xl font-fredoka font-bold text-white">Parent PIN required</h2>
      <p className="text-white/50 text-sm mt-1 mb-5">Enter your PIN to open <span className="text-white/80 font-bold">{label}</span>.</p>
      <input
        ref={inputRef}
        type="password"
        inputMode="numeric"
        value={pin}
        onChange={(e) => { setPin(e.target.value.replace(/\D/g, "").slice(0, 4)); setError(""); }}
        placeholder="••••"
        maxLength={4}
        data-testid={`${testId}-pin-input`}
        className="w-40 mx-auto block bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white text-2xl text-center tracking-[0.6em] font-mono outline-none focus:border-amber-500/40"
      />
      {error && <p className="text-red-300 text-xs mt-3" data-testid={`${testId}-error`}>{error}</p>}
      <button type="submit" disabled={busy || pin.length !== 4} data-testid={`${testId}-submit`}
        className="w-full mt-5 py-3 rounded-xl bg-amber-500 text-white font-fredoka font-bold text-sm hover:bg-amber-400 transition-all disabled:opacity-40 flex items-center justify-center gap-2">
        {busy ? "Checking..." : <><ShieldCheck size={16} /> Unlock</>}
      </button>
      <button type="button" onClick={onForgot} data-testid={`${testId}-forgot`}
        className="text-white/40 text-xs mt-4 hover:text-white/70 transition-all inline-flex items-center gap-1">
        <Mail size={12} /> Forgot your PIN?
      </button>
      <p className="text-white/25 text-[10px] mt-3">You can also log out and reset it in Parent Controls → Safety.</p>
    </form>
  );
}

function ResetPin({ parentId, onBack, onDone, testId }) {
  const [step, setStep] = useState("request"); // request | confirm
  const [hint, setHint] = useState("");
  const [debugCode, setDebugCode] = useState("");
  const [code, setCode] = useState("");
  const [newPin, setNewPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const requestCode = async () => {
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/parent/pin/reset-request`, { parent_id: parentId });
      setHint(r.data?.email_hint || "");
      if (r.data?.debug_code) setDebugCode(r.data.debug_code);
      setStep("confirm");
    } catch (err) { showApiError(err, "send reset code"); setError(err.response?.data?.detail || "Couldn't send the code."); }
    finally { setBusy(false); }
  };

  const confirm = async (disable) => {
    if (code.length !== 6) { setError("Enter the 6-digit code from your email."); return; }
    if (!disable && newPin.length !== 4) { setError("Choose a new 4-digit PIN."); return; }
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/parent/pin/reset-confirm`, {
        parent_id: parentId, code, new_pin: disable ? "" : newPin,
      });
      onDone();
    } catch (err) { setError(err.response?.data?.detail || "That code is invalid."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`${testId}-reset`}>
      <button onClick={onBack} className="text-white/40 text-xs hover:text-white/70 inline-flex items-center gap-1 mb-3" data-testid={`${testId}-reset-back`}>
        <ArrowLeft size={12} /> Back to PIN entry
      </button>
      <div className="w-14 h-14 rounded-2xl bg-cyan-500/20 border border-cyan-400/30 flex items-center justify-center mx-auto mb-4">
        <Mail size={26} className="text-cyan-300" />
      </div>
      {step === "request" ? (
        <>
          <h2 className="text-xl font-fredoka font-bold text-white">Reset your PIN</h2>
          <p className="text-white/50 text-sm mt-1 mb-5">We&apos;ll email a 6-digit code to the address on your account so you can set a new PIN.</p>
          <button onClick={requestCode} disabled={busy} data-testid={`${testId}-reset-send`}
            className="w-full py-3 rounded-xl bg-cyan-500 text-white font-fredoka font-bold text-sm hover:bg-cyan-400 transition-all disabled:opacity-40">
            {busy ? "Sending..." : "Email me a reset code"}
          </button>
          {error && <p className="text-red-300 text-xs mt-3">{error}</p>}
        </>
      ) : (
        <>
          <h2 className="text-xl font-fredoka font-bold text-white">Enter your code</h2>
          <p className="text-white/50 text-sm mt-1 mb-4">Sent to {hint || "your email"}.</p>
          {debugCode && <p className="text-amber-300/80 text-[11px] mb-3">Email isn&apos;t configured here, your code is <b className="font-mono">{debugCode}</b></p>}
          <input type="text" inputMode="numeric" value={code} maxLength={6}
            onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
            placeholder="6-digit code" data-testid={`${testId}-reset-code`}
            className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white text-lg text-center tracking-[0.4em] font-mono outline-none focus:border-cyan-500/40 mb-3" />
          <label className="text-white/40 text-[10px] font-bold block mb-1.5 text-left">New 4-digit PIN</label>
          <input type="password" inputMode="numeric" value={newPin} maxLength={4}
            onChange={(e) => { setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4)); setError(""); }}
            placeholder="••••" data-testid={`${testId}-reset-newpin`}
            className="w-32 bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white text-lg text-center tracking-[0.5em] font-mono outline-none focus:border-cyan-500/40 mb-3" />
          {error && <p className="text-red-300 text-xs mb-3" data-testid={`${testId}-reset-error`}>{error}</p>}
          <button onClick={() => confirm(false)} disabled={busy} data-testid={`${testId}-reset-confirm`}
            className="w-full py-3 rounded-xl bg-emerald-500 text-white font-fredoka font-bold text-sm hover:bg-emerald-400 transition-all disabled:opacity-40 flex items-center justify-center gap-2">
            {busy ? "Saving..." : <><Check size={16} /> Set new PIN &amp; unlock</>}
          </button>
          <button onClick={() => confirm(true)} disabled={busy} data-testid={`${testId}-reset-disable`}
            className="w-full mt-2 py-2.5 rounded-xl border border-white/10 text-white/50 text-xs font-bold hover:bg-white/5 transition-all">
            Turn the PIN lock off instead
          </button>
        </>
      )}
    </div>
  );
}
